import React, { useState } from 'react';
import styled from 'styled-components';
import Button from './Button';

const FormContainer = styled.div`
  background-color: var(--white);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--box-shadow);
  padding: 2rem;
`;

const Form = styled.form`
  display: grid;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: var(--gray-dark);
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: var(--border-radius);
  font-size: 1rem;
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: var(--blue);
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.25);
  }
`;

const Textarea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: var(--border-radius);
  font-size: 1rem;
  min-height: 150px;
  transition: border-color 0.2s;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: var(--blue);
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.25);
  }
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: var(--border-radius);
  font-size: 1rem;
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: var(--blue);
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.25);
  }
`;

const Alert = styled.div<{ type: 'success' | 'error' }>`
  padding: 1rem;
  margin-bottom: 1.5rem;
  border-radius: var(--border-radius);
  background-color: ${({ type }) => type === 'success' ? 'rgba(40, 167, 69, 0.1)' : 'rgba(220, 53, 69, 0.1)'};
  color: ${({ type }) => type === 'success' ? 'var(--green)' : 'var(--red)'};
  border: 1px solid ${({ type }) => type === 'success' ? 'var(--green)' : 'var(--red)'};
`;

interface ContactFormProps {
  formspreeEndpoint?: string;
}

const ContactForm: React.FC<ContactFormProps> = ({ 
  formspreeEndpoint = 'https://formspree.io/f/your-endpoint' 
}) => {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');
  
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('submitting');
    
    try {
      const form = e.currentTarget;
      const formData = new FormData(form);
      
      const response = await fetch(formspreeEndpoint, {
        method: 'POST',
        body: formData,
        headers: {
          Accept: 'application/json',
        },
      });
      
      if (response.ok) {
        setStatus('success');
        setMessage('¡Gracias por tu mensaje! Nos pondremos en contacto contigo lo antes posible.');
        form.reset();
      } else {
        throw new Error('Ha ocurrido un error al enviar el formulario.');
      }
    } catch (error) {
      setStatus('error');
      setMessage('Lo sentimos, ha ocurrido un error al enviar el formulario. Por favor, inténtalo de nuevo más tarde.');
    }
  };
  
  return (
    <FormContainer>
      {status === 'success' && (
        <Alert type="success">{message}</Alert>
      )}
      
      {status === 'error' && (
        <Alert type="error">{message}</Alert>
      )}
      
      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="name">Nombre *</Label>
          <Input 
            type="text" 
            id="name" 
            name="name" 
            required 
            placeholder="Escribe tu nombre" 
          />
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="email">Email *</Label>
          <Input 
            type="email" 
            id="email" 
            name="email" 
            required 
            placeholder="Escribe tu email" 
          />
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="phone">Teléfono</Label>
          <Input 
            type="tel" 
            id="phone" 
            name="phone" 
            placeholder="Escribe tu teléfono" 
          />
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="subject">Asunto *</Label>
          <Select id="subject" name="subject" required>
            <option value="">Selecciona una opción</option>
            <option value="web-basica">Web Básica</option>
            <option value="web-estandar">Web Estándar</option>
            <option value="web-avanzada">Web Avanzada</option>
            <option value="landing-page">Landing Page</option>
            <option value="consultoria">Consultoría</option>
            <option value="otro">Otro</option>
          </Select>
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="message">Mensaje *</Label>
          <Textarea 
            id="message" 
            name="message" 
            required 
            placeholder="¿En qué podemos ayudarte?" 
          />
        </FormGroup>
        
        <Button 
          type="submit" 
          disabled={status === 'submitting'} 
          fullWidth
        >
          {status === 'submitting' ? 'Enviando...' : 'Enviar mensaje'}
        </Button>
      </Form>
    </FormContainer>
  );
};

export default ContactForm; 